import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages the free shipping progress bar.
 * 
 * @extends {Component}
 */
class FreeShippingComponent extends Component {
  #hasInitialized = false;

  connectedCallback() {
    super.connectedCallback();
    
    // 設定値(円建て)を取得
    this.maxValue = parseInt(this.getAttribute('max-value') || '10000', 10);
    
    // 通貨記号を取得
    this.currencySymbol = this.getAttribute('data-currency-symbol') || '¥';
    
    // 達成時のメッセージを取得
    this.textAchieved = this.getAttribute('data-text-achieved') || 'Free shipping achieved!';
    
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    
    // ダイアログの開閉を監視してアニメーションをリセットする
    this.#setupDrawerObserver();
    
    this.updateProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    if (this.observer) this.observer.disconnect();
  }

  #setupDrawerObserver() {
    const dialog = this.closest('dialog') || this.closest('cart-drawer-component')?.querySelector('dialog');
    if (!dialog) return;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          if (dialog.hasAttribute('open')) {
            this.#animateFromZero();
          }
        }
      });
    });

    this.observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  #animateFromZero() {
    this.#updateUI(0, 0, 0, 0, { noTransition: true });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateProgress();
      });
    });
  }

  #handleCartUpdate = (event) => {
    console.log('[FreeShipping] CartUpdate event received:', event.detail);
    
    const cart = event.detail?.resource;
    if (cart && typeof cart.total_price !== 'undefined') {
      this.updateProgress(cart.total_price);
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.updateProgress();
        });
      });
    }
  };

  updateProgress(total) {
    if (typeof total === 'undefined') {
        const totalEl = document.querySelector('.cart__total-value');
        if (!totalEl) return;
        
        const valueAttr = totalEl.getAttribute('value');
        if (valueAttr) {
          total = parseInt(valueAttr.replace(/[^\d]/g, ''), 10);
        } else {
          total = parseInt(totalEl.textContent.replace(/[^\d]/g, ''), 10);
        }
        
        if (isNaN(total)) return;
    }

    const currencyRate = window.Shopify?.currency?.rate || 1.0;
    const activeCurrency = window.Shopify?.currency?.active || 'JPY';
    const isZeroDecimal = ['JPY', 'KRW', 'VND'].includes(activeCurrency);
    
    if (currencyRate <= 0) return;

    const totalInBase = total / (isZeroDecimal ? 1 : 100) / currencyRate;
    
    if (isNaN(totalInBase) || !isFinite(totalInBase)) return;

    const cappedRatio = Math.min(totalInBase / this.maxValue, 1);
    const barPercent = cappedRatio * 100;
    const imagePercent = cappedRatio * 85;
    const triPercent = Math.max(6, cappedRatio * 90);
    
    this.#updateUI(totalInBase, this.maxValue, barPercent, imagePercent, triPercent);
  }

  #updateUI(totalInBase, thresholdBase, bar, image, tri, options = {}) {
    const messageContainerEl = this.querySelector('.free-shipping-message');
    const imageEl = this.querySelector('.progress-bar-fill-image');
    const barFillEl = this.querySelector('.progress-bar-fill');
    const triEl = this.querySelector('.progress-bar-fill-tri');

    if (!messageContainerEl) return;

    const transitionStyle = options.noTransition ? 'none' : '';
    [barFillEl, imageEl, triEl].forEach(el => {
      if (el) el.style.transition = transitionStyle;
    });

    if (totalInBase >= thresholdBase) {
      if (!this.achievedMessageEl) {
        this.achievedMessageEl = document.createElement('div');
        this.achievedMessageEl.className = 'free-shipping-achieved-text';
        this.achievedMessageEl.innerHTML = this.textAchieved;
        this.achievedMessageEl.style.display = 'none';
        messageContainerEl.appendChild(this.achievedMessageEl);
      }
      
      if (!this.progressMessageWrapper) {
        this.progressMessageWrapper = document.createElement('div');
        this.progressMessageWrapper.className = 'free-shipping-progress-text';
        while (messageContainerEl.firstChild && messageContainerEl.firstChild !== this.achievedMessageEl) {
          this.progressMessageWrapper.appendChild(messageContainerEl.firstChild);
        }
        messageContainerEl.insertBefore(this.progressMessageWrapper, this.achievedMessageEl);
      }

      messageContainerEl.classList.add('active');
      this.progressMessageWrapper.style.display = 'none';
      this.achievedMessageEl.style.display = 'block';
      if (imageEl) imageEl.classList.add('active');
    } else {
      messageContainerEl.classList.remove('active');
      if (this.progressMessageWrapper) this.progressMessageWrapper.style.display = 'block';
      if (this.achievedMessageEl) this.achievedMessageEl.style.display = 'none';
      if (imageEl) imageEl.classList.remove('active');
      
      const remainingInBase = thresholdBase - totalInBase;
      const currencyRate = window.Shopify?.currency?.rate || 1.0;
      const activeCurrency = window.Shopify?.currency?.active || 'JPY';
      const isZeroDecimal = ['JPY', 'KRW', 'VND'].includes(activeCurrency);
      
      const remainingInActiveSmallestUnit = Math.round(remainingInBase * currencyRate * (isZeroDecimal ? 1 : 100));
      
      // remainingEl は messageContainerEl の外側に元々あっても探せるように、this (コンポーネント全体) から探す
      const remainingEl = this.querySelector('.remaining-amount');
      
      if (remainingEl) {
        if (window.Shopify && window.Shopify.formatMoney) {
          remainingEl.textContent = window.Shopify.formatMoney(remainingInActiveSmallestUnit);
        } else {
          // フォールバック
          const formatted = (remainingInActiveSmallestUnit / (isZeroDecimal ? 1 : 100)).toLocaleString();
          remainingEl.textContent = this.currencySymbol + formatted;
        }
      }
    }

    if (barFillEl) barFillEl.style.width = `${bar}%`;
    if (imageEl) imageEl.style.left = `${image}%`;
    if (triEl) triEl.style.left = `${tri}%`;
  }
}

if (!customElements.get('free-shipping-component')) {
  customElements.define('free-shipping-component', FreeShippingComponent);
}
